import { defineComponent as _defineComponent } from 'vue';
import { renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createVNode as _createVNode, normalizeClass as _normalizeClass, normalizeStyle as _normalizeStyle } from "vue";
const _hoisted_1 = ["onClick"];
import { ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import VoucherCard from './VoucherCard.vue';
const normalOverlap = 70;
const tightOverlap = 10;
export default /*@__PURE__*/ _defineComponent({
    __name: 'MGWalletStack',
    props: {
        allActiveVouchers: {},
        formatter: {},
        showInitialReveal: { type: Boolean }
    },
    setup(__props) {
        const props = __props;
        const router = useRouter();
        const ungroupedMerchantId = ref(null);
        const expandedCardId = ref(null);
        const showInitialAnimation = ref(false);
        const isRegrouping = ref(false);
        const expandedMerchantId = ref(null);
        function getTextColorForBackground(bg) {
            let r = 0, g = 0, b = 0;
            if (bg.startsWith('rgb')) {
                const match = bg.match(/\d+/g);
                if (match)
                    [r, g, b] = match.map(Number);
            }
            else if (bg.startsWith('#')) {
                if (bg.length === 7) {
                    r = parseInt(bg.slice(1, 3), 16);
                    g = parseInt(bg.slice(3, 5), 16);
                    b = parseInt(bg.slice(5, 7), 16);
                }
            }
            const brightness = (r * 299 + g * 587 + b * 114) / 1000;
            return brightness > 200 ? 'var(--text-color)' : 'var(--light-text-color)';
        }
        const groupedVouchers = computed(() => {
            const groups = {};
            props.allActiveVouchers.forEach(v => {
                const id = v.discount.merchant.id;
                if (!groups[id]) {
                    groups[id] = {
                        merchant: v.discount.merchant,
                        vouchers: [],
                        total: 0,
                        count: 0,
                        firstVoucher: v
                    };
                }
                groups[id].vouchers.push(v);
                groups[id].total += v.currentBalance;
                groups[id].count++;
            });
            return Object.values(groups).map((g) => {
                return {
                    ...g.firstVoucher,
                    groupCount: g.count > 1 ? g.count : null,
                    groupTotal: g.count > 1 ? g.total : null
                };
            });
        });
        const computedDisplayStack = computed(() => {
            if (!ungroupedMerchantId.value) {
                const sortedVouchers = [...props.allActiveVouchers].sort((a, b) => {
                    return a.discount.merchant.id.localeCompare(b.discount.merchant.id);
                });
                const result = [];
                let currentMerchant = null;
                let currentMerchantCards = [];
                for (const voucher of sortedVouchers) {
                    if (currentMerchant !== voucher.discount.merchant.id) {
                        if (currentMerchantCards.length > 0) {
                            const isExpanded = expandedMerchantId.value === currentMerchant;
                            const cardsToShow = isExpanded ? currentMerchantCards : currentMerchantCards.slice(0, 3);
                            result.push(...cardsToShow);
                        }
                        currentMerchant = voucher.discount.merchant.id;
                        currentMerchantCards = [voucher];
                    }
                    else {
                        currentMerchantCards.push(voucher);
                    }
                }
                if (currentMerchantCards.length > 0) {
                    const isExpanded = expandedMerchantId.value === currentMerchant;
                    const cardsToShow = isExpanded ? currentMerchantCards : currentMerchantCards.slice(0, 3);
                    result.push(...cardsToShow);
                }
                return result;
            }
            const stack = [];
            groupedVouchers.value.forEach((groupedVoucher) => {
                if (groupedVoucher.discount.merchant.id === ungroupedMerchantId.value) {
                    stack.push(...props.allActiveVouchers.filter(v => v.discount.merchant.id === ungroupedMerchantId.value));
                }
                else {
                    stack.push(groupedVoucher);
                }
            });
            return stack;
        });
        const stackHeight = computed(() => {
            const cardHeight = 200;
            const extraPadding = 0;
            let cumulativePosition = 0;
            for (let i = 0; i < computedDisplayStack.value.length - 1; i++) {
                const currentCard = computedDisplayStack.value[i];
                const nextCard = computedDisplayStack.value[i + 1];
                if (nextCard && currentCard.discount.merchant.id === nextCard.discount.merchant.id) {
                    cumulativePosition += tightOverlap;
                }
                else {
                    cumulativePosition += normalOverlap;
                }
            }
            return cumulativePosition + cardHeight + extraPadding;
        });
        function triggerInitialAnimation() {
            if (props.showInitialReveal === false) {
                return;
            }
            if (groupedVouchers.value.length > 0) {
                const firstGroup = groupedVouchers.value[0];
                if (firstGroup.groupCount && firstGroup.groupCount > 1) {
                    setTimeout(() => {
                        showInitialAnimation.value = true;
                        ungroupedMerchantId.value = firstGroup.discount.merchant.id;
                        setTimeout(() => {
                            isRegrouping.value = true;
                            setTimeout(() => {
                                ungroupedMerchantId.value = null;
                                setTimeout(() => {
                                    showInitialAnimation.value = false;
                                    isRegrouping.value = false;
                                }, 400);
                            }, 50);
                        }, 1500);
                    }, 1000);
                }
                else {
                    const grouped = groupedVouchers.value.filter((v) => v.groupCount && v.groupCount > 1);
                    if (grouped.length > 0) {
                        const firstGroupedVoucher = grouped[0];
                        setTimeout(() => {
                            showInitialAnimation.value = true;
                            ungroupedMerchantId.value = firstGroupedVoucher.discount.merchant.id;
                            setTimeout(() => {
                                isRegrouping.value = true;
                                setTimeout(() => {
                                    ungroupedMerchantId.value = null;
                                    setTimeout(() => {
                                        showInitialAnimation.value = false;
                                        isRegrouping.value = false;
                                    }, 400);
                                }, 50);
                            }, 1000);
                        }, 500);
                    }
                }
            }
        }
        function getCardStyle(index, cardId, cardColour) {
            const isExpanded = expandedCardId.value === cardId;
            const expandedCardIndex = computedDisplayStack.value.findIndex((card) => card.id === expandedCardId.value);
            let backgroundColor = cardColour || 'var(--background-color-vourcher-default)';
            let color = getTextColorForBackground(backgroundColor);
            let cumulativePosition = 0;
            for (let i = 0; i < index; i++) {
                const currentCard = computedDisplayStack.value[i];
                const nextCard = computedDisplayStack.value[i + 1];
                if (nextCard && currentCard.discount.merchant.id === nextCard.discount.merchant.id && currentCard.discount.merchant.id !== expandedMerchantId.value) {
                    cumulativePosition += tightOverlap;
                }
                else {
                    cumulativePosition += normalOverlap;
                }
            }
            let topPosition;
            if (isExpanded) {
                const expandedPosition = Math.max(cumulativePosition - 30, 20);
                topPosition = `${expandedPosition}px`;
            }
            else if (expandedCardId.value && index > expandedCardIndex) {
                const extraOffset = 110;
                topPosition = `${cumulativePosition + extraOffset}px`;
            }
            else {
                topPosition = `${cumulativePosition}px`;
            }
            let style = {
                position: 'absolute',
                top: topPosition,
                width: isExpanded ? '104%' : '100%',
                height: '200px',
                zIndex: isExpanded ? 999 : index,
                background: backgroundColor,
                color: color,
                transition: getTransitionStyle(isExpanded, index, expandedCardIndex)
            };
            if (isExpanded) {
                style.left = '-2%';
                style.right = 'auto';
                style.margin = '0';
            }
            else {
                style.left = 0;
                style.right = 0;
                style.margin = '0 auto';
            }
            return style;
        }
        function getTransitionStyle(isExpanded, currentIndex, expandedCardIndex) {
            if (isRegrouping.value) {
                return 'top 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94), ' +
                    'width 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)';
            }
            if (isExpanded) {
                return 'top 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94), ' +
                    'width 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94), ' +
                    'z-index 0.1s ease-out 0.2s';
            }
            else if (expandedCardId.value && currentIndex > expandedCardIndex) {
                const delay = (currentIndex - expandedCardIndex) * 0.02;
                return `top 0.25s cubic-bezier(0.25, 0.46, 0.45, 0.94) ${delay}s`;
            }
            else {
                return 'top 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94), ' +
                    'width 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)';
            }
        }
        function goToVoucher(voucher) {
            router.push('/MyVouchers/Merchants/' + voucher.id + '?fromWallet=true');
        }
        function handleCardClick(v) {
            if (showInitialAnimation.value) {
                return;
            }
            if (v.isSkeleton) {
                return;
            }
            const merchantId = v.discount.merchant.id;
            const merchantVouchers = props.allActiveVouchers.filter((av) => av.discount.merchant.id === merchantId);
            const isGrouped = merchantVouchers.length > 1;
            const isTopCard = computedDisplayStack.value[computedDisplayStack.value.length - 1].id === v.id;
            if (isTopCard) {
                if (isGrouped && expandedMerchantId.value !== merchantId) {
                    expandedMerchantId.value = merchantId;
                    expandedCardId.value = null;
                    return;
                }
                if (expandedCardId.value !== null || (expandedMerchantId.value !== null && expandedMerchantId.value !== merchantId)) {
                    expandedCardId.value = null;
                    expandedMerchantId.value = null;
                    return;
                }
                goToVoucher(v);
                return;
            }
            if (isGrouped) {
                if (expandedMerchantId.value === merchantId) {
                    if (expandedCardId.value === v.id) {
                        goToVoucher(v);
                    }
                    else {
                        expandedCardId.value = v.id;
                    }
                }
                else {
                    expandedMerchantId.value = merchantId;
                    expandedCardId.value = null;
                }
            }
            else {
                if (expandedMerchantId.value) {
                    expandedMerchantId.value = null;
                    expandedCardId.value = null;
                }
                if (expandedCardId.value === v.id) {
                    goToVoucher(v);
                }
                else {
                    expandedCardId.value = v.id;
                }
            }
        }
        function getCardCount(v) {
            const merchantId = v.discount.merchant.id;
            const merchantVouchers = props.allActiveVouchers.filter((av) => av.discount.merchant.id === merchantId);
            return merchantVouchers.length > 1 ? merchantVouchers.length : null;
        }
        function getGroupAmount(v) {
            const merchantId = v.discount.merchant.id;
            const merchantVouchers = props.allActiveVouchers.filter((av) => av.discount.merchant.id === merchantId);
            if (merchantVouchers.length > 1) {
                return merchantVouchers.reduce((total, mv) => total + mv.currentBalance, 0);
            }
            return null;
        }
        onMounted(() => {
            triggerInitialAnimation();
        });
        return (_ctx, _cache) => {
            return (_openBlock(), _createElementBlock("div", {
                class: "walletStack",
                style: _normalizeStyle({ minHeight: stackHeight.value + 'px' })
            }, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(computedDisplayStack.value, (v, i) => {
                    return (_openBlock(), _createElementBlock("div", {
                        key: v.id,
                        class: _normalizeClass(["walletCard", {
                                expanded: expandedCardId.value === v.id,
                                'initial-animation': showInitialAnimation.value && expandedCardId.value === v.id
                            }]),
                        style: _normalizeStyle(getCardStyle(i, v.id, v.discount.cardColour)),
                        onClick: ($event) => (handleCardClick(v))
                    }, [
                        _createVNode(VoucherCard, {
                            voucher: v,
                            formatter: _ctx.formatter,
                            wallet: true,
                            obscured: !(expandedCardId.value === v.id || i === computedDisplayStack.value.length - 1),
                            cardCount: getCardCount(v) && expandedMerchantId.value !== v.discount.merchant.id ? getCardCount(v) : null,
                            totalGroupAmount: getGroupAmount(v) && expandedMerchantId.value !== v.discount.merchant.id ? getGroupAmount(v) : null
                        }, null, 8, ["voucher", "formatter", "obscured", "cardCount", "totalGroupAmount"])
                    ], 14, _hoisted_1));
                }), 128))
            ], 4));
        };
    }
});
